import { disableNetwork, enableNetwork } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Put Firestore back on the network when the app comes back to the foreground.
 *
 * A phone freezes a backgrounded page and the connection behind every listener
 * dies with it. Nothing announces that. Because this app keeps a persistent
 * local cache, the listeners carry on answering out of it, so the screen looks
 * alive and current while being neither — and the only way anyone found to get
 * real data was to kill the app and start it again, which is a page load and
 * therefore a new connection.
 *
 * Turning the network off and on again is the documented way to force that
 * reconnect without a reload. Every listener re-runs against the server and
 * the screen catches up on whatever arrived while the phone was asleep.
 */

/** Below this, the page was almost certainly never frozen. */
const STALE_AFTER_MS = 30_000;

let hiddenAt: number | null = null;
let running = false;
let expectingBuild = false;
let reloading = false;

/**
 * Take a new build while the app is in the background.
 *
 * An installed app is opened once and then resumed for weeks. Resuming is not
 * navigating, so it never asks for index.html again, never notices a new
 * worker, and goes on running whatever it was launched with — which is why
 * every deploy so far has needed the app killed from the switcher to appear.
 *
 * So the check is made on the way back in, and the page is reloaded only if
 * one is actually waiting. Only ever after time away, never under someone
 * mid-sentence: controllerchange also fires the first time a worker takes
 * over a page that had none, which is not an update and must not reload.
 */
/** How often to ask, while the app is open and being looked at. */
const ASK_EVERY_MS = 60_000;

let announce: (() => void) | null = null;

function watchForNewBuild(): void {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;

    /*
     * Coming back into the app is a moment where a reload costs nothing, so
     * it just happens. Finding one while somebody is using the app is not:
     * they get told, and choose. Reloading out from under a half-typed
     * comment to save them four seconds is not a trade worth making.
     */
    if (expectingBuild) {
      reloading = true;
      window.location.reload();
      return;
    }

    void offerIfNewer(navigator.serviceWorker.controller);
  });
}

declare const __SW_VERSION__: string;
const RUNNING = typeof __SW_VERSION__ === 'string' ? __SW_VERSION__ : '0.0.0';

/**
 * Offered only when what took over is newer than what is running.
 *
 * Loading a new build fetches the page from the server, so it is already the
 * latest -- and then the worker catches up to the same build a moment later,
 * which also fires a takeover. Offered every time, that asked again for the
 * build already on screen, once for each deploy since: tap after tap to get
 * somewhere the first tap had already gone. A worker too old to say which it
 * is predates every page that asks, so it is never newer.
 */
export async function offerIfNewer(
  worker: Pick<ServiceWorker, 'postMessage'> | null,
  running: string = RUNNING,
): Promise<void> {
  const version = await versionOf(worker);
  if (version !== null && isNewer(version, running)) announce?.();
}

/** Asks a worker which build it is; null if it does not answer. */
export function versionOf(
  worker: Pick<ServiceWorker, 'postMessage'> | null,
  waitMs = 3000,
): Promise<string | null> {
  if (!worker) return Promise.resolve(null);
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), waitMs);
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      channel.port1.close();
      resolve(typeof event.data === 'string' ? event.data : null);
    };
    worker.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
  });
}

/** Whether version a is later than b, part by part: 0.92.10 is after 0.92.9. */
export function isNewer(a: string, b: string): boolean {
  const left = a.split('.').map(Number);
  const right = b.split('.').map(Number);
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const difference = (left[i] ?? 0) - (right[i] ?? 0);
    if (difference !== 0) return difference > 0;
  }
  return false;
}

async function askForNewBuild(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  await registration?.update().catch(() => undefined);
}

/**
 * Keeps asking whether there is a new build, rather than only finding out on
 * the way back into the app.
 *
 * `onReady` is called when one has taken over while the app was open and in
 * use — the app offers it, and the reload happens when the offer is accepted.
 */
export function watchForUpdates(onReady: () => void): () => void {
  announce = onReady;

  const ask = () => {
    if (document.visibilityState !== 'visible') return;
    void askForNewBuild();
  };

  // Once on launch, in case the phone was asleep through a deploy, then on a
  // slow loop for as long as the app is up.
  ask();
  const timer = window.setInterval(ask, ASK_EVERY_MS);

  return () => {
    window.clearInterval(timer);
    announce = null;
  };
}

export function resyncOnResume(): void {
  watchForNewBuild();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }

    const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
    hiddenAt = null;

    // A glance at the notification shade is not worth tearing the connection
    // down for, and reconnecting while a reconnect is in flight is worse than
    // doing nothing.
    if (away < STALE_AFTER_MS) return;

    // Whatever shipped while the phone was asleep. Separate from the
    // reconnect below, which must still happen even if this finds nothing.
    void askForNewBuild();

    if (running) return;

    running = true;
    void (async () => {
      try {
        await disableNetwork(db);
        await enableNetwork(db);
      } catch {
        // Offline, or the cache is busy. The next resume tries again, and the
        // SDK reconnects on its own if it can.
      } finally {
        running = false;
      }
    })();
  });
}
